// src/features/companies/new.tsx

import { Form } from "@/components/ui/form";
import { Card, CardBody, CardFooter, CardHeader, Divider } from "@heroui/react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { z } from "zod";

import {
  AutocompleteAsyncField,
  CheckboxField,
  InputField,
  SelectField,
  TextareaField,
} from "@/features/FormCard/Fields";

import { FormActionsNew } from "@/features/FormCard/Actions";
import {
  CancelConfirmationModal,
  ConfirmationModal,
} from "@/features/FormCard/Modals";

import { useEditForm } from "@/features/Edit/hooks";
import { NewDiff } from "@/features/FormCard/NewDiff";

import { preprocessTextAsyncAutocompleteField } from "@/features/FormCard/utils";

// Import select options from filter.tsx
import { categoryOptions } from "./filter";

const client = "instit-quantilia";
const table_name = "companies";

// Zod schema for the form
const FormSchema = z.object({
  // Required fields
  key: z.string().min(1, "Key is required"),
  name: z.string().min(1, "Name is required"),

  // Flags
  is_client: z.boolean().optional(),
  is_provider: z.boolean().optional(),
  is_etf_provider: z.boolean().optional(),
  is_fund_provider: z.boolean().optional(),
  is_index_provider: z.boolean().optional(),

  // Categorisation
  category: z.string().nullable().optional(),
  max_issuer_count: z.preprocess(
    (val) =>
      val === "" || val === null || val === undefined ? null : Number(val),
    z.number().optional().nullable(),
  ),

  // Contact emails (comma-separated)
  emails_contact: z.string().nullable().optional(),
  emails_complain: z.string().nullable().optional(),

  // Address & location
  address1: z.string().nullable().optional(),
  city: z.string().nullable().optional(),
  country: z.string().trim().min(1, "Country is required"),

  // Parent company (autocomplete)
  parent_company_name: z.preprocess(
    (e: any) => preprocessTextAsyncAutocompleteField(e),
    z
      .union([
        z.string().optional().nullable(),
        z.object({
          id: z.number(),
          name: z.string(),
          text: z.string(),
        }),
      ])
      .optional(),
  ),
  parent_company_id: z.preprocess(
    (val) =>
      val === "" || val === null || val === undefined ? null : Number(val),
    z.number().optional().nullable(),
  ),

  // Other contact & metadata
  description: z.string().nullable().optional(),
});

export function CompaniesNew() {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const {
    form,
    handleReset,
    onSubmit,
    confirmUpdate,
    cancelUpdate,
    isConfirmationModalOpen,
    isDeleteModalOpen,
    openDeleteModal,
    closeDeleteModal,
    isUpdating,
    changedFields,
  } = useEditForm({
    client,
    table_name,
    FormSchema,
    isNew: true,
    disable_translations_request: true,
  });

  const navigateToList = () => navigate(-1);

  return (
    <div className="grid place-items-center h-full w-full">
      <div className="flex justify-center w-full max-w-card">
        <Card className="p-4 bg-white w-full">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <CardHeader className="text-lg font-bold text-gray-700">
                New Company
              </CardHeader>
              <Divider />
              <CardBody className="flex flex-col gap-4">
                {/* Row 1: Key, Name */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="key"
                    label={t(`${table_name}.columns.key`, {
                      ns: "tables",
                      defaultValue: "Key",
                    })}
                    isRequired
                    control={form.control}
                  />
                  <InputField
                    name="name"
                    label={t(`${table_name}.columns.name`, {
                      ns: "tables",
                      defaultValue: "Name",
                    })}
                    isRequired
                    control={form.control}
                    capitalize={true}
                  />
                </div>

                {/* Row 2: Client, Provider */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <CheckboxField
                    name="is_client"
                    label={t(`${table_name}.columns.is_client`, {
                      ns: "tables",
                      defaultValue: "Is Client",
                    })}
                    control={form.control}
                  />
                  <CheckboxField
                    name="is_provider"
                    label={t(`${table_name}.columns.is_provider`, {
                      ns: "tables",
                      defaultValue: "Is Provider",
                    })}
                    control={form.control}
                  />
                </div>

                {/* Row 3: ETF, Fund */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <CheckboxField
                    name="is_etf_provider"
                    label={t(`${table_name}.columns.is_etf_provider`, {
                      ns: "tables",
                      defaultValue: "Is ETF Provider",
                    })}
                    control={form.control}
                  />
                  <CheckboxField
                    name="is_fund_provider"
                    label={t(`${table_name}.columns.is_fund_provider`, {
                      ns: "tables",
                      defaultValue: "Is Fund Provider",
                    })}
                    control={form.control}
                  />
                </div>

                {/* Row 4: Index, Category */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <CheckboxField
                    name="is_index_provider"
                    label={t(`${table_name}.columns.is_index_provider`, {
                      ns: "tables",
                      defaultValue: "Is Index Provider",
                    })}
                    control={form.control}
                  />
                  <SelectField
                    name="category"
                    label={t(`${table_name}.columns.category`, {
                      ns: "tables",
                      defaultValue: "Category",
                    })}
                    options={categoryOptions}
                    control={form.control}
                    allow_empty_option
                  />
                </div>

                {/* Row 5: Max Issuer Count, Contact Emails */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="max_issuer_count"
                    label={t(`${table_name}.columns.max_issuer_count`, {
                      ns: "tables",
                      defaultValue: "Max Issuer Count",
                    })}
                    type="number"
                    control={form.control}
                  />
                  <InputField
                    name="emails_contact"
                    label={t(`${table_name}.columns.emails_contact`, {
                      ns: "tables",
                      defaultValue: "Contact Emails",
                    })}
                    control={form.control}
                  />
                </div>

                {/* Row 6: Complain Emails, Address */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="emails_complain"
                    label={t(`${table_name}.columns.emails_complain`, {
                      ns: "tables",
                      defaultValue: "Complain Emails",
                    })}
                    control={form.control}
                  />
                  <InputField
                    name="address1"
                    label={t(`${table_name}.columns.address1`, {
                      ns: "tables",
                      defaultValue: "Address",
                    })}
                    control={form.control}
                  />
                </div>

                {/* Row 7: City, Country */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="city"
                    label={t(`${table_name}.columns.city`, {
                      ns: "tables",
                      defaultValue: "City",
                    })}
                    control={form.control}
                  />
                  <InputField
                    name="country"
                    label={t(`${table_name}.columns.country`, {
                      ns: "tables",
                      defaultValue: "Country",
                    })}
                    isRequired
                    control={form.control}
                  />
                </div>

                {/* Row 8: Parent Company */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <AutocompleteAsyncField
                    name="parent_company_name"
                    label={t(`${table_name}.columns.parent_company_name`, {
                      ns: "tables",
                      defaultValue: "Parent Company",
                    })}
                    base_url="instit-quantilia/companies"
                    column_name="name"
                    id_column="parent_company_id"
                    control={form.control}
                  />
                </div>

                {/* Row 9: Description (full width) */}
                <div className="grid grid-cols-1 gap-4">
                  <TextareaField
                    name="description"
                    label={t(`${table_name}.columns.description`, {
                      ns: "tables",
                      defaultValue: "Description",
                    })}
                    control={form.control}
                    placeholder={t("form.placeholders.description")}
                  />
                </div>
              </CardBody>

              <CardFooter>
                <FormActionsNew
                  onReset={handleReset}
                  onDelete={openDeleteModal}
                />
              </CardFooter>
            </form>
          </Form>

          <ConfirmationModal
            isOpen={isConfirmationModalOpen}
            isLoading={isUpdating}
            onClose={cancelUpdate}
            onConfirm={confirmUpdate}
            title={t("confirm_create")}
          >
            {changedFields
              ? NewDiff(changedFields, ["parent_company_id"])
              : "No changes detected"}
          </ConfirmationModal>

          <CancelConfirmationModal
            isOpen={isDeleteModalOpen}
            onClose={closeDeleteModal}
            onCancel={navigateToList}
          />
        </Card>
      </div>
    </div>
  );
}
